import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "../lib/auth";
import { mcpClient } from "../lib/mcp-client";

const env = import.meta.env as { VITE_MCP_ENDPOINT?: string };
const mcpEndpoint = env.VITE_MCP_ENDPOINT ?? "https://dev.kuvio.ai/mcp";
export const workerOrigin = mcpEndpoint.replace(/\/mcp$/, "");

export interface ConnectionTool {
  name: string;
  description?: string;
}

export interface ConnectionConfig {
  enabledToolsets?: string[];
}

export interface Connection {
  connection_id: string;
  name: string;
  url: string;
  auth_type: "none" | "bearer" | "header" | "oauth2";
  auth_header?: string;
  status: "connected" | "error" | "pending";
  tool_count: number;
  tools?: ConnectionTool[];
  config?: ConnectionConfig;
  server_name?: string;
  server_version?: string;
  server_instructions?: string;
  last_tested?: string;
  error_message?: string;
  /** Sprint 24.5 — set when this connection was registered from a first-party template. */
  first_party_template_slug?: string | null;
}

export function useConnections() {
  return useQuery({
    queryKey: ["connections"],
    queryFn: async (): Promise<Connection[]> => {
      const result = await mcpClient.toolsCall<{ connections: Connection[] }>(
        "list_connections",
        {}
      );
      if (result.isError) throw new Error("Failed to fetch connections");
      return (result.structuredContent as { connections: Connection[] })?.connections ?? [];
    },
    staleTime: 30_000,
  });
}

export function useConnection(connectionId: string | undefined) {
  return useQuery({
    queryKey: ["connection", connectionId],
    queryFn: async (): Promise<Connection> => {
      if (!connectionId) throw new Error("connection_id required");
      const result = await mcpClient.toolsCall<{ connection: Connection }>(
        "get_connection",
        { connection_id: connectionId }
      );
      if (result.isError) throw new Error("Failed to fetch connection");
      return (result.structuredContent as { connection: Connection }).connection;
    },
    enabled: !!connectionId,
    staleTime: 30_000,
  });
}

export function useAddConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      name: string;
      url: string;
      auth_type: "none" | "bearer" | "header" | "oauth2";
      auth_header?: string;
      auth_value?: string;
      oauth_client_id?: string;
      oauth_client_secret?: string;
      oauth_authorization_url?: string;
      oauth_token_url?: string;
      oauth_scopes?: string;
      oauth_pkce?: boolean;
      oauth_first_party?: boolean;
      oauth_provider?: string;
      enabled_toolsets?: string[];
    }) => {
      const result = await mcpClient.toolsCall<{ connection: Connection }>(
        "add_connection",
        params
      );
      if (result.isError) throw new Error("Failed to add connection");
      return (result.structuredContent as { connection: Connection }).connection;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["connections"] });
    },
  });
}

export function useTestConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (connectionId: string) => {
      const result = await mcpClient.toolsCall(
        "test_connection",
        { connection_id: connectionId }
      );
      if (result.isError) throw new Error("Failed to test connection");
    },
    onSuccess: (_, connectionId) => {
      qc.invalidateQueries({ queryKey: ["connections"] });
      qc.invalidateQueries({ queryKey: ["connection", connectionId] });
    },
  });
}

export interface OAuthTokenStatus {
  authorized: boolean;
  expires_at: string | null;
  scopes: string | null;
}

export function useOAuthStatus() {
  return useQuery({
    queryKey: ["oauth-status"],
    queryFn: async (): Promise<Record<string, OAuthTokenStatus>> => {
      const resp = await authFetch(`${workerOrigin}/oauth/status`);
      if (!resp.ok) throw new Error("Failed to fetch OAuth status");
      const data = (await resp.json()) as { tokens: Record<string, OAuthTokenStatus> };
      return data.tokens ?? {};
    },
    staleTime: 30_000,
  });
}

export function useRemoveConnection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (connectionId: string) => {
      const result = await mcpClient.toolsCall(
        "remove_connection",
        { connection_id: connectionId }
      );
      if (result.isError) throw new Error("Failed to remove connection");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["connections"] });
    },
  });
}

export function useUpdateConnectionConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      connection_id: string;
      enabled_toolsets: string[];
    }) => {
      const resp = await authFetch(`${workerOrigin}/connections/update-config`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(params),
      });
      if (!resp.ok) throw new Error("Failed to update config");
      return (await resp.json()) as { connection: Connection };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["connections"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Sprint 24 — unified connections (MCP + Wrapped) + agent equipment junction
// ---------------------------------------------------------------------------

export type ConnectionKind = "mcp" | "wrapped";

/** The shared status vocabulary rendered by `<ConnectionStatusPill>` (Q12). */
export type UnifiedConnectionStatus =
  | "connected"
  | "active"
  | "oauth-needed"
  | "credentials-needed"
  | "error"
  | "disabled";

interface UnifiedConnectionBase {
  connection_id: string;
  name: string;
  status: UnifiedConnectionStatus;
  /** tool_count (MCP) or operation_count (Wrapped). */
  count: number;
  /** Live agents equipped with this connection. */
  equipped_count: number;
  first_party_template_slug: string | null;
  /** Spec 10.1B follow-on — false = muted (tools hidden from every endpoint). */
  enabled: boolean;
}

export interface UnifiedMcpConnection extends UnifiedConnectionBase {
  kind: "mcp";
  url: string;
  auth_type: string;
  server_name: string | null;
}

export interface UnifiedWrappedConnection extends UnifiedConnectionBase {
  kind: "wrapped";
  auth_kind: string;
  auth_kind_scope: string;
  base_url: string;
  my_credential_set: boolean;
}

export type UnifiedConnection = UnifiedMcpConnection | UnifiedWrappedConnection;

/** Whether a connection is registered from a first-party template (Sprint 24.5). */
export function isFirstParty(c: Pick<UnifiedConnection, "first_party_template_slug">): boolean {
  return !!c.first_party_template_slug;
}

/** The `<ConnectionTypeChip>` `type` for a unified connection (folds in first-party). */
export function chipTypeFor(
  c: Pick<UnifiedConnection, "kind" | "first_party_template_slug">,
): "mcp" | "mcp-first-party" | "wrapped" | "wrapped-first-party" {
  if (c.kind === "mcp") return isFirstParty(c) ? "mcp-first-party" : "mcp";
  return isFirstParty(c) ? "wrapped-first-party" : "wrapped";
}

/** The `<EquippedWithChip>` dot kind (first-party dominates type) for a connection. */
export function equippedDotKind(
  c: Pick<UnifiedConnection, "kind" | "first_party_template_slug">,
): "first-party" | "mcp" | "wrapped" {
  return isFirstParty(c) ? "first-party" : c.kind;
}

export function useUnifiedConnections() {
  return useQuery({
    queryKey: ["connections-unified"],
    queryFn: async (): Promise<UnifiedConnection[]> => {
      const result = await mcpClient.toolsCall<{ connections: UnifiedConnection[] }>(
        "list_connections_unified",
        {},
      );
      if (result.isError) throw new Error("Failed to fetch connections");
      return (result.structuredContent as { connections: UnifiedConnection[] })?.connections ?? [];
    },
    staleTime: 20_000,
  });
}

/** Spec 10.1B follow-on — mute/unmute a connection (hides its tools; keeps it). */
export function useSetConnectionEnabled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      connection_kind: ConnectionKind;
      connection_id: string;
      enabled: boolean;
    }) => {
      const result = await mcpClient.toolsCall("set_connection_enabled", params);
      if (result.isError) throw new Error("Failed to update connection");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["connections-unified"] });
    },
  });
}

export interface EquippedAgent {
  node_id: string;
  name: string;
  avatar_file_id: string | null;
  role: string | null;
}

export function useAgentsEquippedWith(
  kind: ConnectionKind | undefined,
  connectionId: string | undefined,
) {
  return useQuery({
    queryKey: ["agents-equipped-with", kind, connectionId],
    queryFn: async (): Promise<EquippedAgent[]> => {
      if (!kind || !connectionId) return [];
      const result = await mcpClient.toolsCall<{ agents: EquippedAgent[] }>(
        "list_agents_equipped_with",
        { connection_kind: kind, connection_id: connectionId },
      );
      if (result.isError) throw new Error("Failed to fetch equipped agents");
      return (result.structuredContent as { agents: EquippedAgent[] })?.agents ?? [];
    },
    enabled: !!kind && !!connectionId,
    staleTime: 20_000,
  });
}

export interface AgentEquipmentEdge {
  node_id: string;
  connection_kind: ConnectionKind;
  connection_id: string;
  scoped_tools_json: string | null;
  created_at: string;
}

export function useAgentEquipment(nodeId: string | undefined) {
  return useQuery({
    queryKey: ["agent-equipment", nodeId],
    queryFn: async (): Promise<AgentEquipmentEdge[]> => {
      if (!nodeId) return [];
      const result = await mcpClient.toolsCall<{ equipment: AgentEquipmentEdge[] }>(
        "list_agent_equipment",
        { node_id: nodeId },
      );
      if (result.isError) throw new Error("Failed to fetch agent equipment");
      return (result.structuredContent as { equipment: AgentEquipmentEdge[] })?.equipment ?? [];
    },
    enabled: !!nodeId,
    staleTime: 20_000,
  });
}

function invalidateEquipment(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["connections-unified"] });
  qc.invalidateQueries({ queryKey: ["agent-equipment"] });
  qc.invalidateQueries({ queryKey: ["agents-equipped-with"] });
}

export function useEquipAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      node_id: string;
      connection_kind: ConnectionKind;
      connection_id: string;
      scoped_tools?: string[];
    }) => {
      const result = await mcpClient.toolsCall("equip_agent_with_connection", params);
      if (result.isError) throw new Error("Failed to equip agent");
    },
    onSuccess: () => invalidateEquipment(qc),
  });
}

export function useUnequipAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      node_id: string;
      connection_kind: ConnectionKind;
      connection_id: string;
    }) => {
      const result = await mcpClient.toolsCall("unequip_agent_from_connection", params);
      if (result.isError) throw new Error("Failed to unequip agent");
    },
    onSuccess: () => invalidateEquipment(qc),
  });
}
